/**
 * Gift Wrap Fraud Protection
 * Fraud tespiti yaparak dolandırıcılık girişimlerini engellemek için
 * NOT: Amerika'dan normal siparişler alınabilir, sadece fraud tespit edilirse engellenir
 */

// Gift Wrap ürününü tanımlama (ürün adı veya handle ile)
const GIFT_WRAP_PRODUCT_HANDLES = ['gift-wrap-1', 'gift-wrap', 'giftwrap', 'gift_wrap'];
const GIFT_WRAP_PRODUCT_TITLES = ['Gift Wrap', 'Giftwrap', 'Gift Wrap'];

// Fraud tespit kriterleri
const FRAUD_DETECTION = {
  // IP ve shipping address arasındaki maksimum mesafe (km)
  // Shopify'ın tespit ettiği: 3509 km
  MAX_DISTANCE_KM: 3000, // 3000 km'den fazla mesafe şüpheli
  
  // Proxy/VPN tespiti için
  CHECK_PROXY: true,
  
  // Eksik bilgi kontrolü
  REQUIRE_CVV: false, // Client-side'da CVV'ye erişemeyiz, bu server-side kontrol edilir
  REQUIRE_BILLING_ADDRESS: false // Client-side'da billing address'e erişemeyiz
};

// Hata mesajları
const ERROR_MESSAGES = {
  en: 'Your order has been flagged for security review. Please contact customer service to complete your purchase.',
  tr: 'Siparişiniz güvenlik incelemesi için işaretlendi. Lütfen satın alma işlemini tamamlamak için müşteri hizmetleri ile iletişime geçin.'
};

/**
 * IP ve coğrafi bilgileri al
 */
async function getIPLocation() {
  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      return {
        country: data.country_code,
        city: data.city,
        region: data.region,
        latitude: data.latitude,
        longitude: data.longitude,
        isProxy: data.proxy === true || data.vpn === true || data.tor === true,
        isp: data.org
      };
    }
  } catch (error) {
    console.warn('Could not determine IP location:', error);
  }
  return null;
}

/**
 * İki koordinat arasındaki mesafeyi hesapla (Haversine formülü)
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Dünya yarıçapı (km)
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Mesafe (km)
}

/**
 * Shipping address koordinatlarını al (geocoding)
 */
async function getShippingAddressCoordinates(address) {
  if (!address || !address.city || !address.country) {
    return null;
  }

  try {
    // Nominatim (OpenStreetMap) geocoding servisi kullan
    const query = encodeURIComponent(`${address.city}, ${address.country}`);
    const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`, {
      headers: {
        'User-Agent': 'Shopify-Theme-Fraud-Protection'
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        };
      }
    }
  } catch (error) {
    console.warn('Could not geocode shipping address:', error);
  }
  return null;
}

/**
 * Ürünün Gift Wrap olup olmadığını kontrol et
 */
function isGiftWrapProduct(product) {
  if (!product) return false;

  const productHandle = product.handle?.toLowerCase() || '';
  const productTitle = product.title?.toLowerCase() || '';
  const variantTitle = product.variant_title?.toLowerCase() || '';

  // Handle kontrolü - önce tam eşleşme, sonra includes kontrolü
  for (const handle of GIFT_WRAP_PRODUCT_HANDLES) {
    const handleLower = handle.toLowerCase();
    // Tam eşleşme veya handle içeriyor mu kontrol et
    if (productHandle === handleLower || productHandle.includes(handleLower)) {
      return true;
    }
  }

  // Title kontrolü
  for (const title of GIFT_WRAP_PRODUCT_TITLES) {
    const titleLower = title.toLowerCase();
    if (productTitle.includes(titleLower) || variantTitle.includes(titleLower)) {
      return true;
    }
  }

  // Product ID veya variant ID ile kontrol (isteğe bağlı)
  // Eğer Gift Wrap ürününün ID'sini biliyorsanız buraya ekleyebilirsiniz
  // if (product.product_id === YOUR_GIFT_WRAP_PRODUCT_ID) return true;

  return false;
}

/**
 * Cart'ta Gift Wrap ürünü var mı kontrol et
 */
async function hasGiftWrapInCart() {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    if (response.ok) {
      const cart = await response.json();
      return cart.items?.some(item => isGiftWrapProduct(item));
    }
  } catch (error) {
    console.warn('Could not check cart for Gift Wrap:', error);
  }
  return false;
}

/**
 * Gift Wrap ürünü için fraud kontrolü yap
 */
async function checkGiftWrapFraud(product = null) {
  // Eğer ürün belirtilmişse, Gift Wrap olup olmadığını kontrol et
  if (product && !isGiftWrapProduct(product)) {
    return { allowed: true, isFraud: false };
  }

  // Cart'ta Gift Wrap var mı kontrol et
  const hasGiftWrap = product ? isGiftWrapProduct(product) : await hasGiftWrapInCart();
  
  if (!hasGiftWrap) {
    return { allowed: true, isFraud: false };
  }

  // Fraud risk skoru
  let fraudScore = 0;
  const fraudReasons = [];

  // 1. IP lokasyonu ve proxy kontrolü
  const ipLocation = await getIPLocation();
  
  if (ipLocation) {
    // Proxy/VPN kontrolü
    if (FRAUD_DETECTION.CHECK_PROXY && ipLocation.isProxy) {
      fraudScore += 3;
      fraudReasons.push('High risk internet connection (proxy/VPN) detected');
    }

    // 2. IP ve shipping address mesafesi kontrolü
    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        
        // Shipping address bilgilerini al
        const shippingAddress = {
          city: cart.attributes?.shipping_city || '',
          country: cart.attributes?.shipping_country || '',
          zip: cart.attributes?.shipping_zip || ''
        };

        if (shippingAddress.city && shippingAddress.country && ipLocation.latitude && ipLocation.longitude) {
          // Shipping address koordinatlarını al
          const shippingCoords = await getShippingAddressCoordinates(shippingAddress);
          
          if (shippingCoords) {
            // Mesafeyi hesapla
            const distance = calculateDistance(
              ipLocation.latitude,
              ipLocation.longitude,
              shippingCoords.latitude,
              shippingCoords.longitude
            );

            if (distance > FRAUD_DETECTION.MAX_DISTANCE_KM) {
              fraudScore += 4;
              fraudReasons.push(`Shipping address is ${Math.round(distance)} km from IP location`);
            }
          }
        }
      }
    } catch (error) {
      console.warn('Could not check shipping address distance:', error);
    }
  } else {
    // IP lokasyonu tespit edilemezse, düşük risk
    fraudScore += 1;
  }

  // 3. Hızlı sipariş kontrolü (aynı IP'den çok sayıda deneme)
  const orderAttempts = sessionStorage.getItem('giftWrapOrderAttempts') || 0;
  if (parseInt(orderAttempts) > 3) {
    fraudScore += 2;
    fraudReasons.push('Multiple order attempts detected');
  }

  // Fraud skoru değerlendirmesi
  // Skor 5 veya üzeri ise fraud olarak kabul et
  const isFraud = fraudScore >= 5;

  if (isFraud) {
    // Deneme sayısını artır
    sessionStorage.setItem('giftWrapOrderAttempts', parseInt(orderAttempts) + 1);
    
    return {
      allowed: false,
      isFraud: true,
      message: ERROR_MESSAGES.en,
      reasons: fraudReasons,
      score: fraudScore
    };
  }

  return { 
    allowed: true, 
    isFraud: false,
    score: fraudScore,
    reasons: fraudReasons.length > 0 ? fraudReasons : []
  };
}

/**
 * Hata mesajını göster
 */
function showErrorMessage(message) {
  // Mevcut hata mesajı varsa kaldır
  const existingError = document.querySelector('.gift-wrap-error-message');
  if (existingError) {
    existingError.remove();
  }

  // Yeni hata mesajı oluştur
  const errorDiv = document.createElement('div');
  errorDiv.className = 'gift-wrap-error-message';
  errorDiv.style.cssText = `
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
    padding: 12px 16px;
    border-radius: 4px;
    margin: 16px 0;
    font-size: 14px;
    line-height: 1.5;
  `;
  errorDiv.textContent = message;

  return errorDiv;
}

/**
 * Product form submit'i engelle
 */
function interceptProductForm() {
  document.addEventListener('submit', async function(event) {
    const form = event.target;
    
    // Product form mu kontrol et
    if (!form.closest('product-form') || form.tagName !== 'FORM') {
      return;
    }

    // Form içindeki variant ID'yi al
    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput || !variantIdInput.value) {
      return;
    }

    // Ürün bilgilerini al
    const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    if (!productHandle) {
      return;
    }

    // Ürünün Gift Wrap olup olmadığını kontrol et
    const product = {
      handle: productHandle,
      title: document.querySelector('h1')?.textContent || '',
      product_id: form.querySelector('[data-product-id]')?.getAttribute('data-product-id')
    };

    const check = await checkGiftWrapFraud(product);
    
    if (!check.allowed) {
      event.preventDefault();
      event.stopPropagation();

      // Hata mesajını göster
      const errorDiv = showErrorMessage(check.message);
      const productForm = form.closest('product-form');
      if (productForm) {
        // Mevcut hata mesajını kaldır
        const existingError = productForm.querySelector('.gift-wrap-error-message');
        if (existingError) {
          existingError.remove();
        }
        
        // Form'un üstüne ekle
        const formElement = productForm.querySelector('form');
        if (formElement) {
          formElement.insertBefore(errorDiv, formElement.firstChild);
        } else {
          productForm.insertBefore(errorDiv, productForm.firstChild);
        }
        
        // Scroll to error
        setTimeout(() => {
          errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }

      // Loading state'i kaldır
      const submitButtons = form.querySelectorAll('button[type="submit"]');
      submitButtons.forEach(button => {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      });

      // Theme loading event'ini sonlandır
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      return false;
    }
  }, true); // Capture phase'de çalış
}

/**
 * Cart'a ekleme işlemini engelle
 */
function interceptCartAdd() {
  // Custom event listener ekle - variant:add event'ini dinle
  document.addEventListener('variant:add', async function(event) {
    const items = event.detail?.items || [];
    
    // Eklenen ürünlerden biri Gift Wrap mi kontrol et
    for (const item of items) {
      if (isGiftWrapProduct(item)) {
        const check = await checkGiftWrapFraud(item);
        
        if (!check.allowed) {
          // Event'i durdur
          event.stopPropagation();
          event.preventDefault();
          
          // Cart'tan ürünü kaldır
          try {
            await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                id: item.key || item.id,
                quantity: 0
              })
            });
          } catch (error) {
            console.warn('Could not remove Gift Wrap from cart:', error);
          }
          
          // Hata mesajını göster
          const errorDiv = showErrorMessage(check.message);
          const productForm = document.querySelector('product-form');
          if (productForm) {
            const existingError = productForm.querySelector('.gift-wrap-error-message');
            if (existingError) {
              existingError.remove();
            }
            productForm.insertBefore(errorDiv, productForm.firstChild);
            errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          // Cart'ı yenile
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          
          return false;
        }
      }
    }
  }, true);

  // Fetch intercept - daha güvenli kontrol için
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const url = args[0];
    
    // Cart add endpoint'ine istek yapılıyor mu kontrol et
    if (typeof url === 'string' && url.includes('/cart/add.js')) {
      const options = args[1] || {};
      
      if (options.method === 'POST') {
        let variantId = null;
        let productHandle = null;
        
        if (options.body instanceof FormData) {
          variantId = options.body.get('id');
        } else if (typeof options.body === 'string') {
          try {
            const bodyData = JSON.parse(options.body);
            variantId = bodyData.id;
          } catch (e) {
            // FormData veya başka bir format olabilir
          }
        }
        
        if (variantId) {
          // Product handle'ı mevcut sayfadan al
          productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
          
          if (productHandle) {
            const product = {
              handle: productHandle,
              title: document.querySelector('h1')?.textContent || ''
            };

            const check = await checkGiftWrapFraud(product);
            
            if (!check.allowed) {
              // Hata döndür
              return Promise.resolve({
                ok: false,
                status: 403,
                statusText: 'Forbidden',
                json: async () => ({
                  description: check.message,
                  status: 403
                }),
                text: async () => check.message
              });
            }
          }
        }
      }
    }

    return originalFetch.apply(this, args);
  };
}

/**
 * Checkout öncesi kontrol
 */
function interceptCheckout() {
  // Cart sayfasında checkout butonuna tıklama
  document.addEventListener('click', async function(event) {
    const target = event.target.closest('button[name="checkout"], a[href*="/checkout"], form[action*="/checkout"]');
    
    if (!target) return;

    // Cart'ta Gift Wrap var mı kontrol et
    const hasGiftWrap = await hasGiftWrapInCart();
    
    if (hasGiftWrap) {
      const check = await checkGiftWrapFraud();
      
      if (!check.allowed) {
        event.preventDefault();
        event.stopPropagation();

        // Hata mesajını göster
        const errorDiv = showErrorMessage(check.message);
        
        // Cart drawer veya cart page'de göster
        const cartContainer = document.querySelector('cart-drawer, .cart-page, .main-cart');
        if (cartContainer) {
          cartContainer.insertBefore(errorDiv, cartContainer.firstChild);
          errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          alert(check.message);
        }

        return false;
      }
    }
  }, true);
}

/**
 * Initialize fraud protection
 */
function initGiftWrapFraudProtection() {
  // Sayfa yüklendiğinde çalış
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      interceptProductForm();
      interceptCartAdd();
      interceptCheckout();
    });
  } else {
    interceptProductForm();
    interceptCartAdd();
    interceptCheckout();
  }
}

// Initialize
initGiftWrapFraudProtection();

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    checkGiftWrapFraud,
    isGiftWrapProduct,
    getIPLocation
  };
}

